import Masonry from "react-masonry-css"
import styled from "styled-components/macro"
import { useProjectDataStore } from "@state/projectDataStore"
import { useStore } from "@state/store"
import { Suspense, useEffect, useRef, useState } from "react"
import { getColor } from "@utils/getColor"
import useDragScroll from "@src/hooks/useDragScroll"
import useClickOverDrag from "@src/hooks/useClickOverDrag"

const breakpointColumnsObj = {
  default: 3,
  1100: 3,
  700: 2,
  500: 2,
}

const ImageGridMasonry = ({ galleryData, categories, contentControls }) => {
  const syncState = useStore((s) => s.syncState)
  const images = galleryData?.images

  const scroll = galleryData?.scroll
  const spacing = galleryData?.spacing
  const rounded = galleryData?.rounded
  const style = galleryData?.style
  const colors = galleryData?.colors

  const selectedCategory = categories[contentControls?.index]

  const scrollConfig = {
    damping: 25,
  }

  const { scrollerRef, handlePointerDown, handlePointerUp } =
    useDragScroll(scrollConfig)

  const { handleUp, handleDown } = useClickOverDrag()

  const galleryIndexChange = (index) => {
    syncState({ galleryImagesIndex: index })
  }

  const scrollRight = () => {
    document
      .getElementById("grid")
      .scrollBy({ left: +1000, behavior: "smooth" })
  }

  const scrollLeft = () => {
    document
      .getElementById("grid")
      .scrollBy({ left: -1000, behavior: "smooth" })
  }

  return (
    <MasonryWrapper
      scroll={scroll}
      className="scrollref grid-wrapper"
      colors={colors}
      windowType={style}
      $categoryColor={getColor(galleryData?.categoryColor)}
    >
      {scroll === "vertical" && (
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="masonry-grid"
          columnClassName="masonry-grid_column"
        >
          {images
            .filter(
              (image) =>
                selectedCategory === "All" ||
                image?.category === selectedCategory,
            )
            .map((image, index) => (
              <GridItem key={`${index}`} spacing={spacing}>
                <Suspense fallback={<div>Loading IMAGE...</div>}>
                  <Image
                    onClick={() => galleryIndexChange(index)}
                    src={image?.content?.publicURL}
                    alt={`Image ${image?.copy + 1}`}
                    rounded={rounded}
                    scroll={scroll}
                  />
                </Suspense>
              </GridItem>
            ))}
        </Masonry>
      )}
      {scroll === "horizontal" && (
        <HorizontalScroll
          spacing={spacing}
          id="grid"
          ref={scrollerRef}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
        >
          {[0, 6, 12].map((startIndex, index) => (
            <Block key={startIndex} spacing={spacing} rounded={rounded}>
              {images &&
                images
                  .filter((image) => image?.category === selectedCategory)
                  ?.slice(startIndex, startIndex + 6)
                  ?.map((image, index) => (
                    <img
                      onPointerDown={handleDown}
                      onPointerUp={(e) => {
                        handleUp(e, () =>
                          galleryIndexChange(startIndex + index),
                        )
                      }}
                      key={index}
                      className={`grid-item item${index + 1}`}
                      src={image?.content?.publicURL}
                    />
                  ))}
            </Block>
          ))}
        </HorizontalScroll>
      )}
      {scroll === "horizontal" && (
        <div className="button">
          <div onClick={scrollLeft}>
            {" "}
            <img src="/assets/icons/back.svg" alt="" />
          </div>
          <div onClick={scrollRight}>
            <img
              src="/assets/icons/back.svg"
              style={{ transform: "rotate(180deg)" }}
              alt=""
            />
          </div>
        </div>
      )}
    </MasonryWrapper>
  )
}

export default ImageGridMasonry

const MasonryWrapper = styled.div<{
  scroll
  colors
  windowType: string
  $categoryColor: string
}>`
  width: 100%;
  overflow-y: ${({ scroll }) => (scroll === "horizontal" ? "hidden" : "auto")};
  overflow-x: ${({ scroll }) => (scroll === "horizontal" ? "auto" : "hidden")};
  height: ${({ windowType }) => (windowType === "full" ? "100%" : "100%")};
  padding: ${({ windowType }) => (windowType === "window" ? "9rem 2rem" : "")};
  background-color: #02443980;

  .button {
    display: flex;
    gap: 1rem;
    position: fixed;
    bottom: 0;
    right: 0;
    margin: 2.5rem;
    cursor: pointer;

    div {
      background-color: var(--black);
      padding: 1rem;
      color: #fff;
      width: 4rem;
      height: 4rem;
      border-radius: 2rem;
      img {
        width: 100%;
        height: 100%;
        padding: 0.5rem;
      }
      &:active {
        opacity: 0.75;
        transform: scale(0.95);
        transition: 0.3s;
      }
    }
  }
`

const GridItem = styled.div<{ spacing: string }>`
  position: relative;
  overflow: hidden;
  padding: ${({ spacing }) =>
    spacing === "none"
      ? "0rem"
      : spacing === "large"
      ? "1.75rem"
      : spacing === "medium"
      ? "0.75rem"
      : "0.15rem"};
`

const HorizontalScroll = styled.div<{ spacing: string }>`
  width: auto;
  height: 100%;
  display: flex;
  gap: ${({ spacing }) =>
    spacing === "none"
      ? "0rem"
      : spacing === "large"
      ? "2.75rem"
      : spacing === "medium"
      ? "0.75rem"
      : "0.15rem"};
  overflow-x: scroll;
  overflow-y: hidden;
  padding: 4rem;
  cursor: pointer;
`

const Block = styled.div<{ spacing: string; rounded: string }>`
  display: grid;
  height: 100%;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  grid-auto-flow: column;
  grid-gap: ${({ spacing }) =>
    spacing === "none"
      ? "0rem"
      : spacing === "large"
      ? "2.75rem"
      : spacing === "medium"
      ? "0.75rem"
      : "0.15rem"};

  .grid-item {
    background-color: #ccc;
    border-radius: ${({ rounded }) => (rounded ? "0.75rem" : "5rem")};
    width: 15vw;
    height: 100%;
    object-fit: cover;
    cursor: pointer;

    &:active {
      opacity: 0.75;
      transform: scale(0.99);
      transition: 0.3s;
    }
  }

  .item1 {
    grid-column: span 1;
    grid-row: span 1;
  }

  .item2 {
    grid-column: span 1;
    grid-row: span 1;
  }

  .item3 {
    grid-column: span 1;
    grid-row: span 1;
  }

  .item4 {
    grid-column: span 1;
    grid-row: span 1;
  }
`

const Image = styled.img<{ rounded; scroll }>`
  display: block;
  width: 100%;
  height: ${({ scroll }) => (scroll === "horizontal" ? "50%" : "auto")};
  object-fit: cover;
  transition: 0.3s;
  border-radius: ${({ rounded }) => (rounded ? "0.75rem" : "0rem")};
  cursor: pointer;

  &:active {
    opacity: 0.75;
    transform: scale(0.99);
    transition: 0.3s;
  }
`

const masonryStyles = `
.masonry-grid {
  display: flex;
  margin: 0rem;
  }
`

document.head.insertAdjacentHTML("beforeend", `<style>${masonryStyles}</style>`)
